import embla from 'embla';

const selectors = {
  progressBar: '[data-progress-bar]',
  gallery: '[data-gallery]',
  item: '[data-item]',
  video: 'video',
}

class ComponentUseCases extends HTMLElement {
  #carousel = null;
  #observer = null;
  #currentItem = 0;

  constructor() {
    super();
    
    this.handleScroll = this.handleScroll.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
    this.handleItemPointerEnter = this.handleItemPointerEnter.bind(this);
    this.handleItemPointerLeave = this.handleItemPointerLeave.bind(this);
    this.handleItemIntersection = this.handleItemIntersection.bind(this);
  }

  handleSelect() {
    const activeItem = this.#carousel.selectedScrollSnap();
    
    this.items?.forEach((item, index) => {
      item.dataset.active = index === activeItem;
    });
  }

  handleScroll() {
    const progress = this.#carousel.scrollProgress();
    const items = this.#carousel.slideNodes();
    const itemCount = items.length;

    const minMapped = 1 / itemCount;
    const maxMapped = 1;

    const mappedProgress = minMapped + (maxMapped - minMapped) * progress;

    this.progressBar?.style.setProperty('--progress', mappedProgress);
  }

  isTouchDevice() {
    return window.matchMedia('(pointer: coarse)').matches;
  }

  handleItemPointerEnter(event) {
    if (this.isTouchDevice()) return;

    const video = event.currentTarget.querySelector('video');

    video?.play();
  }

  handleItemPointerLeave(event) {
    if (this.isTouchDevice()) return;

    const video = event.currentTarget.querySelector('video');

    setTimeout(() => {
      if (!video) return;

      if (!video.paused) {
        video.pause();
      }
      
      video.currentTime = 0;
    }, 250)
  }

  handleItemIntersection(event) {
    if (!this.isTouchDevice()) return;

    const step = event.target;
    const video = step.querySelector('video');

    if (!video) return;

    if (event.isIntersecting) {
      video.play();
    }

    if (!event.isIntersecting) {
      video.pause();

      video.currentTime = 0;
    }
  }

  #setupCarousel() {
    this.#carousel = embla(this.gallery, {
      loop: false,
      active: false,
      align: () => 15,
      breakpoints: {
        '(max-width: 800px)': {
          active: true,
        }
      }
    })
  }

  #setupEventListeners() {
    this.#carousel?.on('select', this.handleSelect);
    this.#carousel?.on('scroll', this.handleScroll);
    this.items?.forEach((item) => {
      item.addEventListener('pointerenter', this.handleItemPointerEnter);
      item.addEventListener('pointerleave', this.handleItemPointerLeave);
    });
  }

  #setupObserver() {
    this.#observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        this.handleItemIntersection(entry);
      });
    }, {
      rootMargin: '0px 10px',
      threshold: 0.4
    });

    this.items?.forEach((item) => {
      this.#observer.observe(item);
    });
  }

  connectedCallback() {
    this.container = this;
    this.gallery = this.querySelector(selectors.gallery);
    this.progressBar = this.querySelector(selectors.progressBar);
    this.items = Array.from(this.querySelectorAll(selectors.item));

    this.#setupCarousel();
    this.#setupObserver();
    this.#setupEventListeners();
  }

  disconnectedCallback() {
    this.#carousel.off('scroll', this.handleScroll);
    this.#carousel?.off('select', this.handleSelect);
    this.#observer?.disconnect();

    this.items?.forEach((item) => {
      item.removeEventListener('pointerenter', this.handleItemPointerEnter);
      item.removeEventListener('pointerleave', this.handleItemPointerLeave);
    });

    this.#carousel.destroy();
  }
}

if (!customElements.get('use-cases')) {
  customElements.define('use-cases', ComponentUseCases);
}